type InteractionSource =
  | 'keydown'
  | 'mousemove'
  | 'pointerdown'
  | 'touchstart'
  | 'wheel'
  | 'manual'
  | 'unknown';

type StoreListener = () => void;
type InteractionListener = (source: InteractionSource) => void;

const FIRST_INTERACTION_EVENTS = [
  'keydown',
  'mousemove',
  'pointerdown',
  'touchstart',
  'wheel',
] as const;

let hasInteracted = false;
let lastInteractionSource: InteractionSource = 'unknown';
let cleanupInteractionListeners: (() => void) | null = null;
const storeListeners = new Set<StoreListener>();
const interactionListeners = new Set<InteractionListener>();
const listenersByEvent = new Map<
  (typeof FIRST_INTERACTION_EVENTS)[number],
  () => void
>();

function notifyStoreListeners() {
  for (const listener of storeListeners) {
    listener();
  }
}

function notifyInteractionListeners(source: InteractionSource) {
  const listeners = Array.from(interactionListeners);
  interactionListeners.clear();
  for (const listener of listeners) {
    listener(source);
  }
}

function setHasInteracted(value: boolean, source: InteractionSource) {
  if (hasInteracted === value) {
    return;
  }

  hasInteracted = value;
  lastInteractionSource = source;

  if (value) {
    cleanupInteractionListeners?.();
    cleanupInteractionListeners = null;
    notifyInteractionListeners(source);
  }

  notifyStoreListeners();
}

function ensureInteractionListeners() {
  if (
    cleanupInteractionListeners ||
    hasInteracted ||
    typeof window === 'undefined'
  ) {
    return;
  }

  for (const eventName of FIRST_INTERACTION_EVENTS) {
    const markInteracted = () => {
      setHasInteracted(true, eventName);
    };

    window.addEventListener(eventName, markInteracted, {
      once: true,
      passive: true,
    });
    listenersByEvent.set(eventName, markInteracted);
  }

  cleanupInteractionListeners = () => {
    for (const eventName of FIRST_INTERACTION_EVENTS) {
      const listener = listenersByEvent.get(eventName);
      if (!listener) {
        continue;
      }

      window.removeEventListener(eventName, listener);
    }

    listenersByEvent.clear();
  };
}

export function markFirstPageInteraction(source: InteractionSource = 'manual') {
  setHasInteracted(true, source);
}

export function readHasFirstPageInteraction() {
  return hasInteracted;
}

export function subscribeFirstPageInteraction(listener: StoreListener) {
  storeListeners.add(listener);
  ensureInteractionListeners();

  return () => {
    storeListeners.delete(listener);
  };
}

export function onFirstPageInteraction(listener: InteractionListener) {
  ensureInteractionListeners();

  if (hasInteracted) {
    listener(lastInteractionSource);
    return () => {};
  }

  interactionListeners.add(listener);

  return () => {
    interactionListeners.delete(listener);
  };
}

export function resetFirstPageInteractionForTest() {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error(
      'resetFirstPageInteractionForTest can only be used in tests'
    );
  }

  cleanupInteractionListeners?.();
  cleanupInteractionListeners = null;
  hasInteracted = false;
  lastInteractionSource = 'unknown';
  listenersByEvent.clear();
  storeListeners.clear();
  interactionListeners.clear();
}
